import _ from "lodash";
import path from "src/pagePath.json";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBlockLayout, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import { useApiOperation } from "src/api/hooks";
import { getOrgList } from "src/api/organization/org-api";
import { BaseInputWithSearch, BasePagination, BaseRadio, BaseTooltip } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import useApiLoading from "src/hooks/useApiLoading";
import { BULLET } from "src/utils";
import { MbOrg } from "src/api/organization/org-types";

/**
 * 계약자 검색 팝업 컴포넌트
 * 계약자를 검색하고 선택할 수 있는 팝업 컴포넌트입니다.
 */

const columnHeader: any = [
  {
    Header: "선택",
    accessor: "isSelected",
    width: 80,
    Cell: ({ row, setSelectedOrg, disabledClassification, data, setData }: any) => {
      const changeTrigger = useCallback(() => {
        // 모든 항목의 선택 상태를 해제하고 현재 선택된 항목만 true로 설정
        const updatedData = data.map((item: any) => ({
          ...item,
          isSelected: String(item.id) === String(row.original.id),
        }));
        setData(updatedData);
        setSelectedOrg(row.original);
      }, [row.original, data, setData, setSelectedOrg]);

      const classifications = row.original?.classifications || [];

      return (
        <BaseRadio
          id={`selector${row.original?.id}`}
          name={"isSelected"}
          onChange={changeTrigger}
          checked={row.original?.isSelected}
          disabled={classifications.includes(disabledClassification)}
        />
      );
    },
  },
  {
    Header: "id",
    accessor: "id",
    width: 80,
    Cell: ({ value }: any) => {
      return <BaseNewTabLink path={path.organization.detail.replace(":id", value)} value={value || "-"} />;
    },
  },
  {
    Header: "법인명/상호",
    accessor: "name",
    width: 300,
    Cell: ({ value }: any) => {
      return (
        <div className="w-100 text-left">
          <BaseTooltip contents={value || "-"} />
        </div>
      );
    },
  },
  {
    Header: "대표자/성명",
    accessor: "ceoName",
    width: 120,
    Cell: ({ value }: any) => {
      return (
        <div className="w-100 text-left">
          <BaseTooltip contents={value || "-"} />
        </div>
      );
    },
  },
  {
    Header: "사업자 등록번호",
    accessor: "bizRegNo",
    width: 120,
    Cell: ({ value }: any) => {
      return <BaseTooltip contents={value || "-"} />;
    },
  },
  {
    Header: "최초 등록일",
    accessor: "createdDate",
    width: 120,
    Cell: ({ value }: any) => {
      return <BaseTooltip contents={value} type="date" />;
    },
  },
  {
    Header: "최종 수정일",
    accessor: "lastModifiedDate",
    width: 120,
    Cell: ({ value }: any) => {
      return <BaseTooltip contents={value} type="date" />;
    },
  },
];

/**
 * 컴포넌트 Props 인터페이스
 * @param isOpen - 팝업 표시 여부
 * @param columns - 커스텀 컬럼 정의 (선택적)
 * @param onClick - 클릭 이벤트 핸들러 (선택적)
 * @param onClose - 닫기 이벤트 핸들러 (선택적)
 * @param defaultChecked - 기본 선택된 항목의 ID 배열 (선택적)
 */
type Props = {
  isOpen: boolean;
  columns?: any;
  defaultSearchOption?: {
    classification: "INDIVIDUAL" | "GENERAL_BIZ";
  };
  disabledClassification?: "INDIVIDUAL" | "GENERAL_BIZ";
  // onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onClick?: (selected: MbOrg) => void;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  defaultChecked?: string[];
};

// 검색옵션 정의
const searchClassificationOptions = [
  { value: "", label: "전체" },
  { value: "GENERAL_BIZ", label: "사업자" },
  { value: "INDIVIDUAL", label: "개인" },
];

/**
 * 사업자 검색 팝업 컴포넌트
 * @param props - 컴포넌트 Props
 */
const SearchOrgPopup = (props: Props) => {
  // 사업자 목록 데이터 상태
  const [data, setData] = useState<any>([]);
  // 선택된 사업자 상태
  const [selectedOrg, setSelectedOrg] = useState<any>();
  // 페이지 메타데이터 상태
  const [pageMeta, setPageMeta] = useState<any>({});

  const defaultParams = useMemo(() => {
    return {
      page: 0,
      size: 20,
      search003: "",
      classification: props.defaultSearchOption?.classification || "",
      sort: {
        orders: [{ property: "id", direction: "DESC" }],
      },
    };
  }, [props.defaultSearchOption]);

  // 검색 파라미터 상태
  const [params, setParams] = useState<any>(defaultParams);

  const { isApiLoading } = useApiLoading();
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns: props.columns ? props.columns : columnHeader,
      data: data,
      setSelectedOrg,
      setData,
      disabledClassification: props.disabledClassification,
    },
    useBlockLayout,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useSticky,
    useRowSelect,
  );
  // columns width 의 총 합 (table witdh 를 구하기 위함)
  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);
  //조회
  const { executeAsync: executeAsyncAll } = useApiOperation(getOrgList);

  /**
   * 사업자 선택 처리 함수
   */
  const onClickData = useCallback(() => {
    if (props.onClick) {
      props.onClick(selectedOrg);
    }
  }, [props, selectedOrg]);

  /**
   * 팝업 닫기 처리 함수
   */
  const closeData = useCallback(async () => {
    if (props.onClose) props.onClose(selectedOrg);
  }, [props]);

  /**
   * 사업자 목록 조회 함수
   * @param params - 검색 파라미터
   */
  const fetchData = useCallback(
    async (params: any) => {
      const response: any = await executeAsyncAll(params);
      for (let i = 0; i < response?.data?.data?.content.length; i++) {
        const org = response.data.data.content[i];
        const isDefaultSelected = props.defaultChecked?.includes(String(org.id));

        response.data.data.content[i] = Object.assign(org, {
          isSelected: isDefaultSelected || false,
        });

        // 기본 선택된 항목이 있으면 selected 상태도 설정
        if (isDefaultSelected) {
          setSelectedOrg(org);
        }
      }
      setData(response?.data?.data?.content || []);
      setPageMeta(response?.data?.meta?.pageMeta);
    },
    [executeAsyncAll, props.defaultChecked],
  );

  useEffect(() => {
    if (props.isOpen) {
      fetchData(defaultParams);
      setParams(defaultParams);
    }
  }, [props.isOpen]);

  return (
    <BaseAbstractModal size="xlarge" isOpen={props.isOpen}>
      <>
        <div className="pa30">
          <section className="base-abstract-modal__title flex-center-between pa0">
            <h1>계약자 선택</h1>
            <div className="minmax360 text-left">
              <BaseInputWithSearch
                type="text"
                selectValue={params.classification}
                inputValue={params.search003 || ""}
                stateOptions={searchClassificationOptions}
                setStateValue={(value: string) => {
                  setParams({ ...params, classification: value });
                }}
                onChange={(value: string) => {
                  setParams({ ...params, search003: value });
                }}
                onKeyUp={() => {
                  setParams({ ...params });
                  fetchData({ ...params, ...{ page: 0 } });
                }}
                onSearchClick={() => {
                  setParams({ ...params });
                  fetchData({ ...params, ...{ page: 0 } });
                }}
                onClearClick={() => {
                  setParams({ ...params, search003: "", classification: "" });
                  fetchData({ ...params, ...{ page: 0 } });
                }}
              />
            </div>
          </section>
          <div className="flex-center mt10">
            <p className="font14 text-gray500">{BULLET} 사업자명, 사업자 번호 또는 이름으로 검색할 수 있습니다.</p>
          </div>
        </div>

        {/* table */}
        <section className="base-abstract-modal__contents">
          <div {...getTableProps()} className="base-table view-data-table sticky  px30">
            <div className="header">
              {headerGroups.map((headerGroup) => (
                <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                  {headerGroup.headers.map((header) => {
                    return (
                      <div {...header.getHeaderProps()} className="base-table__th">
                        {header.render("Header")}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div {...getTableBodyProps()} className="body">
              {rows.map((row: any) => {
                prepareRow(row);
                return (
                  <div {...row.getRowProps()} className={`base-table__tr ${row.values.floor ? "bg-gray100" : ""}`}>
                    {row.cells.map((cell: any) => {
                      return (
                        <div {...cell.getCellProps()} className="base-table__td">
                          {cell.render("Cell")}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              <div>
                {rows.length === 0 && (
                  <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                    <div className="base-table__td w-100 text-center">
                      <div className="w-100">{!isApiLoading() && <span>데이터가 없습니다.</span>}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        <section className="px30">
          <BasePagination
            pageIndex={params.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            currentSize={Number(params.size) || 20}
            sizeOption={(sizeValue) => {
              setParams({ ...params, size: sizeValue });
              fetchData({ ...params, size: sizeValue });
            }}
            totalElements={pageMeta?.totalElements || 0}
            goPage={(page: number) => {
              setParams({ ...params, ...{ page } });
              fetchData({ ...params, ...{ page } });
            }}
            children={
              <div className="flex-center">
                <button className="base-btn color-white mr10" onClick={() => closeData()}>
                  취소
                </button>
                <button className="base-btn" onClick={() => onClickData()}>
                  계약자 선택
                </button>
              </div>
            }
          />
        </section>
      </>
    </BaseAbstractModal>
  );
};

export default SearchOrgPopup;
